import { usePersist } from "../../lib/usePersist";
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, Loader2, Info } from "lucide-react";
import { http, errMsg } from "../../lib/api";

export const Action = ({ a, kid, reload, busy, setBusy, after, tid = "blocker" }) => {
  const key = `${kid}-${a.label}`;
  if (a.type === "link") return (
    <Link to={`/kits/${kid}?tab=${a.tab}`} data-testid={`${tid}-link-${key}`} className="rounded-sm border border-stone-300 px-2.5 py-1 text-xs font-semibold text-stone-700 hover:border-[#9A7B2C] hover:text-[#9A7B2C] transition-colors">{a.label}</Link>
  );
  const go = async () => {
    setBusy(key);
    try { await http.post(a.path); if (after) await after(); toast.success(`${a.label} — started`); await reload(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(null); }
  };
  return (
    <button data-testid={`${tid}-action-${key}`} onClick={go} disabled={!!busy}
      className="inline-flex items-center gap-1 rounded-sm bg-[#2B2A26] px-2.5 py-1 text-xs font-semibold text-white hover:bg-[#9A7B2C] transition-colors disabled:opacity-50">
      {busy === key && <Loader2 size={11} className="animate-spin" />}{a.label}
    </button>
  );
};

const Reason = ({ r, kid, warn, ...p }) => (
  <li className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
    {warn ? <Info size={14} className="text-sky-600" /> : <AlertTriangle size={14} className="text-amber-600" />}
    <span className={warn ? "text-stone-500" : "text-[#2B2A26]"}>{warn && "Warning: "}{r.text}</span>
    {r.actions.map((a) => <Action key={a.label} a={a} kid={kid} {...p} />)}
  </li>
);

export const PublishBlockers = () => {
  const [kits, setKits] = useState(null);
  const [open, setOpen] = usePersist("blockers", true);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => http.get("/kits-bulk/blockers").then((r) => setKits(r.data.kits)).catch(() => {}), []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  if (!kits) return null;
  return (
    <section data-testid="publish-blockers" className="mb-10 rounded-xl border border-amber-200 bg-white p-6">
      <button data-testid="publish-blockers-toggle" onClick={() => setOpen(!open)} className="flex w-full items-center justify-between text-left">
        <div>
          <div className="eyebrow">Publish blockers</div>
          <div data-testid="publish-blockers-count" className="mt-1 text-sm text-stone-600">
            {kits.length ? `${kits.length} kit(s) can't be published yet — each reason has a one-tap fix` : "Every finished kit can be published."}
          </div>
        </div>
        {kits.length > 0 && <ChevronDown size={18} className={`text-stone-400 transition-transform ${open ? "rotate-180" : ""}`} />}
      </button>
      {open && kits.length > 0 && (
        <ul className="mt-4 divide-y divide-stone-100">
          {kits.map((k) => (
            <li key={k.id} data-testid={`blocker-kit-${k.id}`} className="py-3">
              <div className="mb-1 flex items-center gap-2">
                <Link to={`/kits/${k.id}`} className="font-display text-lg text-[#2B2A26] hover:text-[#9A7B2C]">{k.destination}</Link>
                <span className="text-xs text-stone-400">{k.collection === "founders" ? "Founder's" : k.collection} · {k.edition}</span>
                {k.busy && <span className="inline-flex items-center gap-1 text-xs text-[#9A7B2C]"><Loader2 size={11} className="animate-spin" /> working…</span>}
              </div>
              <ul>
                {k.reasons.map((r) => <Reason key={r.text} r={r} kid={k.id} reload={load} busy={busy} setBusy={setBusy} />)}
                {k.warnings.map((r) => <Reason key={r.text} r={r} kid={k.id} warn reload={load} busy={busy} setBusy={setBusy} />)}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
